import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Pre-cache colors once — avoid re-allocating on every frame
const _NEAR_RING  = new THREE.Color('#C9A84C');
const _FAR_RING   = new THREE.Color('#9EC8FF');
const _NEAR_OUTER = new THREE.Color('#E8C870');
const _FAR_OUTER  = new THREE.Color('#B8D8FF');
const _NEAR_FILL  = new THREE.Color('#FFE8C0');
const _FAR_FILL   = new THREE.Color('#C8DEFF');

export default function ArtworkViewingZone({ artwork, isNear, onSelect, isMobile }) {
  const ringRef       = useRef();
  const outerRingRef  = useRef();
  const fillRef       = useRef();
  const smokeGroupRef = useRef();
  const nearLightRef  = useRef();
  const timeRef       = useRef(Math.random() * 8);
  const isLeft = artwork.wall === 'left';
  const cx = artwork.avatarTargetX;
  const cz = artwork.avatarTargetZ;
  const barrierOffset = isLeft ? 1.1 : -1.1;

  // Fewer smoke particles to reduce frame overhead (3 max, 0 on mobile)
  const SMOKE_N = isMobile ? 0 : 3;

  const smokeSeeds = useMemo(() => Array.from({ length: SMOKE_N }, (_, i) => ({
    ox:    Math.sin(i * 1.41 + cz * 0.3) * 0.20,
    oz:    Math.cos(i * 1.88 + cz * 0.3) * 0.20,
    phase: (i / Math.max(SMOKE_N, 1)) * Math.PI * 2,
    spd:   0.50 + i * 0.13,
    sz:    0.28 + (i % 3) * 0.14,
  })), [SMOKE_N, cz]);

  const smokeTex = useMemo(() => {
    if (isMobile) return null;
    const c = document.createElement('canvas');
    c.width = 32; c.height = 32;
    const ctx = c.getContext('2d');
    const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 15);
    g.addColorStop(0, 'rgba(215, 210, 248, 0.80)');
    g.addColorStop(0.5, 'rgba(200, 195, 238, 0.30)');
    g.addColorStop(1, 'rgba(185, 180, 225, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 32, 32);
    return new THREE.CanvasTexture(c);
  }, [isMobile]);

  const sprites = useMemo(() => {
    if (!smokeTex || SMOKE_N === 0) return [];
    return smokeSeeds.map(s => {
      const mat    = new THREE.SpriteMaterial({ map: smokeTex, transparent: true, opacity: 0, depthWrite: false });
      const sprite = new THREE.Sprite(mat);
      sprite.scale.set(s.sz, s.sz, 1);
      return { sprite, mat, seed: s };
    });
  }, [smokeSeeds, smokeTex, SMOKE_N]);

  useEffect(() => {
    const group = smokeGroupRef.current;
    if (!group || sprites.length === 0) return;
    sprites.forEach(({ sprite }) => group.add(sprite));
    return () => {
      sprites.forEach(({ sprite }) => { group.remove(sprite); sprite.material.dispose(); });
    };
  }, [sprites]);

  useFrame((_, delta) => {
    timeRef.current += delta;
    const t     = timeRef.current;
    const pulse = 0.65 + Math.sin(t * 1.3 + cz * 0.08) * 0.35;

    if (ringRef.current) {
      ringRef.current.material.opacity = (isNear ? 0.90 : 0.45) * pulse;
      ringRef.current.material.color.copy(isNear ? _NEAR_RING : _FAR_RING);
    }
    if (outerRingRef.current) {
      outerRingRef.current.material.opacity = (isNear ? 0.40 : 0.16) * pulse;
      outerRingRef.current.material.color.copy(isNear ? _NEAR_OUTER : _FAR_OUTER);
    }
    if (fillRef.current) {
      fillRef.current.material.opacity = (isNear ? 0.16 : 0.06) * pulse;
      fillRef.current.material.color.copy(isNear ? _NEAR_FILL : _FAR_FILL);
    }
    // Smooth near-light intensity — avoids mount/unmount shader rebuild lag
    if (nearLightRef.current) {
      const target = isNear ? 5 : 0;
      nearLightRef.current.intensity += (target - nearLightRef.current.intensity) * Math.min(delta * 4, 1);
    }
    sprites.forEach(({ sprite, mat, seed }) => {
      const tt   = (t * seed.spd + seed.phase) % (Math.PI * 2);
      const life = (Math.sin(tt - Math.PI / 2) + 1) / 2;
      sprite.position.set(seed.ox, 0.08 + life * 1.15, seed.oz);
      mat.opacity = (isNear ? 0.28 : 0.09) * Math.sin(life * Math.PI);
    });
  });

  return (
    <group>
      <mesh ref={ringRef} position={[cx, 0.014, cz]} rotation={[-Math.PI / 2, 0, 0]}
        onClick={(e) => { e.stopPropagation(); onSelect(artwork); }}
        onPointerOver={() => { document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = 'default'; }}
      >
        <ringGeometry args={[0.68, 1.00, 48]} />
        <meshBasicMaterial color="#9EC8FF" transparent opacity={0.45} depthWrite={false} />
      </mesh>
      <mesh ref={outerRingRef} position={[cx, 0.010, cz]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.00, 1.32, 48]} />
        <meshBasicMaterial color="#B8D8FF" transparent opacity={0.16} depthWrite={false} />
      </mesh>
      <mesh ref={fillRef} position={[cx, 0.008, cz]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.68, 48]} />
        <meshBasicMaterial color="#C8DEFF" transparent opacity={0.06} depthWrite={false} />
      </mesh>
      <group ref={smokeGroupRef} position={[cx, 0, cz]} />
      <mesh position={[cx + barrierOffset, 0.006, cz]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.02, artwork.width + 0.7]} />
        <meshBasicMaterial color="#C9A84C" transparent opacity={0.32} depthWrite={false} />
      </mesh>
      {[-1, 1].map(side => (
        <mesh key={side} position={[cx + barrierOffset, 0.007, cz + side * (artwork.width / 2 + 0.35)]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.028, 12]} />
          <meshBasicMaterial color="#C9A84C" transparent opacity={0.55} depthWrite={false} />
        </mesh>
      ))}
      {/* Always-present near-light — intensity smoothed via useFrame to avoid shader rebuild lag */}
      <pointLight ref={nearLightRef} position={[cx, 0.4, cz]} color="#C9A84C" intensity={0} distance={3.2} decay={2} />
    </group>
  );
}
